import { I18n } from '@byted-cg/linkchat-plugin';
import { inject, observer } from 'mobx-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  AppContextType,
  DIALOG_TYPE,
  DIALOG_STATUS,
} from '@byted-cg/linkchat-plugin';
import { Popup, Swiper, RichText, ImageViewer } from '@byted-cg/linkchat-ui';
import { ColumnNotice, Close, Right, Left } from '@byte-design/icons';
import { handleRecognition } from '../utils/richText';
import { parseEmoji } from '../utils/parseEmoji';
import styles from '../theme/default/noticeBar.less';
import { parseQueryString } from '../utils';
import { IAppContext, Lifecycle } from '@byted-cg/linkchat-core';

const { bid, channel, channelId, 'secret-str': secretStr } = parseQueryString();

interface NoticeBarProps {
  appContext?: AppContextType;
  comStyles?: object;
  fetchApi?: any;
  track?: any;
}

interface NoticeItem {
  postCode: string;
  title: string;
  content: string;
}

interface NoticeData {
  data: NoticeItem[];
}

const NoticeBar: React.FC<NoticeBarProps> = ({
  appContext,
  comStyles,
  fetchApi,
  track,
}) => {
  const [showNoticeBar, setShowNoticeBar] = useState(false);
  const [showList, setShowList] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [noticeDetail, setNoticeDetail] = useState<NoticeItem>();
  const [noticeData, setNoticeData] = useState<NoticeData>({
    data: [],
  });
  const [topStyles, setTopStyles] = useState(comStyles);
  let isUserClose = useRef(false);
  let listHeightRef = useRef(null);
  const dialog = appContext?.dialog;
  const isHelpPage = location.href.includes('help.html');

  const verticalItems = noticeData.data.map((item, index) => {
    return (
      <Swiper.Item key={index}>
        <div className={styles['vertical-title']}>{item.title}</div>
      </Swiper.Item>
    );
  });

  const setTopStylesFn = (idx: number) => {
    let isH5 = [12, 42];
    let isPc = [50, 80];
    setTopStyles({
      top: appContext?.query?.isPc ? isPc[idx] : isH5[idx],
    });
  };

  const queryApi = () => {
    try {
      // 获取当前场景 1-机器人 2-人工/排队/离线 3-帮助中心
      let scene = 3;
      let type = Number(appContext?.dialogInfo?.type);
      if (!isHelpPage) {
        type === DIALOG_TYPE.ROBOT && (scene = 1);
        [DIALOG_TYPE.AGENT, DIALOG_TYPE.TRANING, DIALOG_TYPE.OFFLINE].includes(
          type,
        ) && (scene = 2);
      }
      let fetch = fetchApi ?? appContext?.utils?.fetch;
      fetch &&
        fetch('/api/im-session/im-post-config/query', {
          method: 'GET',
          params: {
            scene,
            businessLineId: bid,
            secretReferenceUserId: appContext?.userInfo?.secretStr || secretStr,
            channelId: channel,
            entryId: channelId,
            dialogId: appContext?.dialogInfo?.id || '',
          },
        }).then((data: NoticeItem[]) => {
          if (data?.length) {
            setNoticeData({
              data: [...data],
            });
          }
          setShowNoticeBar(!!data?.length);
        });
    } catch (error) {}
  };

  const handleImgClick = useCallback((event) => {
    try {
      const target = (event.target || event.srcElement) as HTMLLinkElement;
      if (
        target &&
        target.getAttribute &&
        target.nodeName?.toLocaleLowerCase() === 'img'
      ) {
        const url = target?.getAttribute('src') || '';
        if (appContext?.query?.isPc) {
          window.parent.postMessage(
            {
              action: 'previewImg',
              imgUrl: url,
            },
            '*',
          );
        } else {
          ImageViewer.show({
            image: url,
          });
        }
      }
    } catch (error) {
      console.log('error---', error);
    }
  }, []);

  useEffect(() => {
    try {
      // 用户没有主动关闭
      let id = Number(appContext?.dialogInfo?.id);
      if (!isUserClose.current) {
        (isHelpPage || (id && !appContext?.appLoading)) && queryApi();
      }
    } catch (error) {}
  }, [Number(appContext?.dialogInfo?.id), appContext?.appLoading]);

  useEffect(() => {
    if (isHelpPage) return;
    let status = Number(dialog?.ext?.status);
    // 结束会话清掉公告缓存
    if (status === DIALOG_STATUS.ENDING) {
      isUserClose.current = false;
    }
    setTopStylesFn(Number(status === DIALOG_STATUS.QUEUEING));
  }, [Number(dialog?.ext?.status)]);

  // 点击详情按钮
  const callDetail = useCallback(
    (item?: NoticeItem) => {
      if (item?.content || noticeData?.data?.length === 1) {
        setNoticeDetail(item ?? noticeData.data[0]);
        setShowDetail(true);
        // 埋点
        let params = {
          noticeName: item?.title ?? noticeData.data[0].title,
          channelDetailID: channelId,
          businessLineId: bid,
          channelID: channel,
          userId: appContext?.ext?.userId,
          dialogId: appContext?.dialog?.ext?.dialogId,
          createTime: String(Date.now()),
        };
        if (location.href.indexOf('help.html') > -1) {
          delete params.dialogId;
        }
        (track ?? appContext?.utils?.$track)?.({
          event: 'botNotificationClicks',
          params,
        });
      } else {
        setShowList(true);
      }
    },
    [noticeData],
  );
  const closeAllPopup = () => {
    setShowList(false);
    setShowDetail(false);
  };
  const closeNoticeByUser = useCallback(() => {
    setShowNoticeBar(false);
    isUserClose.current = true;
  }, []);

  return showNoticeBar ? (
    <div
      className={
        appContext?.query?.isPc ? styles['notice-bar-pc'] : styles['notice-bar']
      }
      style={topStyles}
    >
      <ColumnNotice
        className={isHelpPage ? styles['left-notice-icon'] : ''}
        theme="filled"
        size="15"
        fill="#338AFF"
      />
      <Swiper
        className={styles['notice-bar-swiper']}
        direction="vertical"
        loop={true}
        style={{ '--height': '40px', '--width': '65%' }}
        indicator={() => null}
        autoplay={noticeData?.data?.length > 1}
      >
        {verticalItems}
      </Swiper>
      <div className={styles['detail-btn']} onClick={() => callDetail()}>
        {I18n.t('details', {}, '详情')}
      </div>
      <div className={styles['close']} onClick={closeNoticeByUser}>
        <Close theme="outline" size="12" fill="#333" />
      </div>
      {/* 多条弹出层 */}
      <Popup
        visible={showList}
        onMaskClick={() => {
          setShowList(false);
        }}
        bodyStyle={{ maxHeight: '86vh', borderRadius: '12px 12px 0 0' }}
      >
        <div ref={listHeightRef} className={styles['list-content']}>
          <div className={styles['list-content-title']}>
            {I18n.t('announcement', {}, '公告')}
          </div>
          <Close
            onClick={() => setShowList(false)}
            className={styles['list-content-close']}
            theme="outline"
            size="16"
            fill="#333"
            strokeWidth={5}
          />
          {/* 列表 */}
          <div className={styles.list}>
            {noticeData.data.length &&
              noticeData.data.map((item, index) => {
                return (
                  <div
                    className={styles.items}
                    onClick={() => {
                      callDetail(item);
                    }}
                  >
                    <div className={styles['items-title']}>{item.title}</div>
                    <Right
                      className={styles['items-icon']}
                      theme="outline"
                      size="18"
                      fill="#969AA0"
                      strokeWidth={3}
                    />
                    {index !== noticeData.data.length - 1 && (
                      <div className={styles['items-divider']}></div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      </Popup>
      {/* 具体内容展示 */}
      <Popup
        visible={showDetail}
        onMaskClick={() => {
          setShowDetail(false);
        }}
        bodyStyle={{
          minHeight: `${listHeightRef?.current?.clientHeight || 0}px`,
          maxHeight: '86vh',
          borderRadius: '12px 12px 0 0',
        }}
      >
        <div className={styles['detail-content']}>
          <div className={styles['detail-content-title']}>
            {I18n.t('announcement', {}, '公告')}
          </div>
          <Left
            onClick={() => setShowDetail(false)}
            className={styles['detail-content-right']}
            theme="outline"
            size="23"
            fill="#333"
            strokeWidth={4}
          />
          <Close
            onClick={closeAllPopup}
            className={styles['detail-content-close']}
            theme="outline"
            size="16"
            fill="#333"
            strokeWidth={5}
          />
          {/* 内容 */}
          <div onClick={handleImgClick} className={styles.container}>
            <div className={styles.contentTitle}>{noticeDetail?.title}</div>
            <RichText
              content={handleRecognition(parseEmoji(noticeDetail?.content, 14))}
              options={{
                ADD_ATTR: ['target'],
                ALLOWED_URI_REGEXP:
                  /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp|xxx|lark|sslocal|aweme):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
              }}
            ></RichText>
          </div>
        </div>
      </Popup>
    </div>
  ) : null;
};

export default inject<{
  appContext: IAppContext;
}>(({ appContext }) => ({
  appContext,
}))(observer(NoticeBar));
